import { ArrowRight, MagnifyingGlass, Money, ShoppingCart, CaretDown } from "@phosphor-icons/react";
import circle_logo from "../assets/images/visaro-logo-circle.svg";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useEffect } from "react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion"; 
import HrPromoSection from "../components/HrPromoSection"; 

// ANIMATION VARIANTS 

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { 
    opacity: 1, 
    x: 0, 
    transition: { duration: 0.8, ease: "easeOut" } 
  }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { 
    opacity: 1, 
    x: 0, 
    transition: { duration: 0.8, ease: "easeOut" } 
  }
};

const faqs = [
  {
    question: "How does Visaro work?",
    answer: "Visaro allows you to split your purchase into 4 equal, interest-free payments. You'll pay the first installment at checkout, and the remaining three payments will be automatically charged to your payment method every two weeks."
  },
  {
    question: "Is there a fee to use Visaro?",
    answer: "There are no upfront fees when you pay on time. Visaro is interest-free, so you only pay what you see at checkout. Late fees may apply if a scheduled payment is missed."
  },
  {
    question: "Do I need to apply for Visaro?",
    answer: "No long application process is required. You simply select Visaro at checkout, provide some basic information, and get an instant decision."
  },
  {
    question: "Will using Visaro affect my credit score?",
    answer: "Using Visaro will not affect your credit score. We perform a soft credit check which does not leave a visible footprint on your credit history."
  },
  {
    question: "What happens if I miss a payment?",
    answer: "If a payment is missed, we will attempt to process it again. If it remains unpaid, a late fee may be charged, and future eligibility for Visaro services may be affected."
  },
  {
    question: "Where can I use Visaro?",
    answer: "You can use Visaro at any of our partner merchants across aviation, health, and retail sectors. Look for the Visaro logo at checkout."
  }
];

const ExpandingHorizonSection = () => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="flex flex-col items-center text-center gap-10 mb-20 max-w-5xl mx-auto px-6 mt-20"
    >
      <div className="flex gap-4 mb-12">
        <Link
          to="/login"
          className="px-10 py-3.5 rounded-lg border border-gray-200 font-bold text-deepBlue hover:bg-gray-50 transition-colors bg-white shadow-sm"
        >
          Login
        </Link>
        <Link
          to="/sign-up"
          className="px-10 py-3.5 rounded-lg bg-deepBlue text-white font-bold hover:bg-opacity-90 transition-opacity shadow-md"
        >
          Get Started
        </Link>
      </div>

      <div className="flex flex-col items-center gap-6 m-12 mt-40">
        <span className="text-visaro-orange font-bold font-aeonic tracking-wide">
          Future Products
        </span>
        
        <h2 className="text-deepBlue text-4xl md:text-6xl font-bold font-aeonic leading-tight">
          Expanding Your Financial <br className="hidden md:block" /> Horizon
        </h2>
        
        <p className="text-gray-500 text-lg md:text-xl max-w-3xl leading-relaxed text-center">
          While we are currently focused on making travelling more accessible through{' '} <br />
          <Link
              to="/sign-up"
              className="text-visaro-orange font-medium hover:underline"
            >
            <span className="text-visaro-orange font-medium">Fly Now, Pay Later</span>.  
            </Link> {' '} 
          
           Visaro is hard at work, building a complete ecosystem for <br />everyday digital life
        </p>
      </div>
    </motion.div>
  );
};

const FAQSection = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

    
    const toggleFAQ = (index: number) => {
      setActiveIndex(activeIndex === index ? null : index);
    };

  return (
    <section className="bg-white py-20 px-6 md:px-32 flex flex-col items-center">
      {/* Header */}
      <div className="text-center mb-16 max-w-2xl">
        <h2 className="text-deepBlue text-3xl md:text-5xl font-bold mb-4 font-aeonic">
          Frequently Asked Questions
        </h2>
        <p className="text-gray-500 text-lg">
          Everything you need to know about Visaro and how it works.
        </p>
      </div>

      {/* Accordion Container */}
      <div className="w-full max-w-3xl flex flex-col gap-4">
        {faqs.map((faq, index) => {
          const isOpen = activeIndex === index;

          return (
            <div 
              key={index} 
              className={`border rounded-xl overflow-hidden transition-colors duration-300 ${
                isOpen ? "border-visaro-orange/50 bg-gray-50/50" : "border-gray-200 bg-white"
              }`}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left focus:outline-none"
              >
                <span className={`font-bold text-lg ${isOpen ? "text-deepBlue" : "text-gray-700"}`}>
                  {faq.question}
                </span>
                <CaretDown 
                  size={24} 
                  weight="bold"
                  className={`transform transition-transform duration-300 ${
                    isOpen ? "rotate-180 text-visaro-orange" : "text-gray-400"
                  }`} 
                />
              </button>

              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="p-6 pt-0 text-gray-500 leading-relaxed">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* Footer / Support Link */}
      <div className="mt-12 text-gray-500 font-medium">
        Still have questions?{" "}
        <a href="/support" className="text-visaro-orange font-bold hover:underline">
          Contact Support
        </a>
      </div>
    </section>
  );
};

const HowItWorksSection = () => {
  return (
    <section className="bg-deepBlue py-20 px-6 md:px-32 w-full">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
        {[
          { icon: <MagnifyingGlass className="text-white w-10 h-10" />, title: "Search & Select", text: "Book your flight through our available aviation partners" },
          { icon: <ShoppingCart className="text-white w-10 h-10" />, title: "Choose Installment Option", text: "Choose your preferred installment option at checkout" },
          { icon: <Money className="text-white w-10 h-10" />, title: "Make Initial Deposit", text: "Make 30% initial deposit and split the remaining balance into 3 monthly installment payments" }
        ].map((item, index) => (
          <motion.div 
            key={index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: index * 0.2 }} 
            className="bg-visaro-gray-700 rounded-lg p-10 flex flex-col items-center text-center"
          >
            <div className="bg-visaro-orange w-20 h-20 rounded-full flex items-center justify-center mb-8">
              {item.icon}
            </div>
            <h3 className="text-white text-2xl font-bold font-aeonic mb-4">
              {item.title}
            </h3>
            <p className="text-gray-300 text-lg leading-relaxed">
              {item.text}
            </p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const HeroSection = () => {
  return (
    <section className="flex flex-col-reverse md:flex-row gap-20 md:gap-40 items-center py-12 md:py-20 px-10 bg-visaro-orange-15 ">
      
      <motion.div 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInLeft}
        className="flex flex-1 md:flex-[1.5] flex-col gap-8"
      >
        <header>
          <div className="text-deepBlue text-4xl md:text-6xl font-bold font-aeonic leading-tight">
            Your Dream Destination <br />
            is Just a Flight Away 
          </div>
        </header>
        
        <div className="text-gray-600 text-lg md:text-2xl leading-relaxed max-w-xl font-aeonic">
          Don't let your budget ground your plans. Book your flights now and spread the cost over manageable monthly installments with Visaro <span className="font-semibold bg-visaro-orange text-white border rounded">Fly Now, Pay Later.</span>
        </div>

        <div className="flex gap-4">
          <Link
              to="/login"
              className="p-3 px-5 border-gray-300 border rounded-lg font-bold text-sm text-deepBlue"
                    >
                      Log in
                    </Link>
          
          <Link
            to="/sign-up"
            className="p-3 px-5 border-visaro-gray-700 border rounded-lg font-bold bg-deepBlue text-white text-sm"
          >
            Get Started
          </Link>
        </div>
      </motion.div>

      <motion.div 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInRight}
        className="flex-1 relative flex justify-center items-center"
      >
        <img
          src={require("../assets/images/visaro-plane-wing.png")}
          alt="Visaro Pay mobile app interface showing Buy Now Pay Later options"
          width="600" 
          height="700"
          fetchPriority="high" 
          className="rounded-3xl object-cover w-full max-w-md md:max-w-full"
        />

        {/* <div 
          aria-hidden="true" 
          className="absolute bottom-16 bg-white/90 backdrop-blur-md border border-white shadow-custom-light rounded-3xl p-4 text-sm flex w-3/4 left-1/2 -translate-x-1/2"
        >
          <img
            src={circle_logo}
            alt="" 
            className="h-10 self-center aspect-square bg-white rounded-full mr-4"
          />
          <div className="flex-1">
            <div className="text-visaro-orange font-bold">Pay Later</div>
            <div className="text-visaro-gray-700 text-xs mt-1">
              3 interest-free payments due every month starting today.
            </div>
          </div>
          <div className="text-visaro-gray-300 text-xs font-medium">now</div>
        </div> */}
      </motion.div>
    </section>
  );
};

const Index = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <div className="overflow-x-hidden"> 
      {/* Top Release Notification Bar - Mobile Optimized */}
      <div className="bg-deepBlue text-white py-2 px-3 sm:px-4 text-center text-xs md:text-sm font-medium flex items-center justify-center flex-wrap sm:flex-nowrap gap-1.5 sm:gap-2 border-b border-visaro-orange/30">
        <span className="bg-visaro-orange text-white text-[10px] uppercase font-bold px-2 py-0.5 rounded-full tracking-wide shrink-0">
          New Release
        </span>
        <span className="sm:hidden text-xs text-gray-200">
          Free HR System &amp; Credit on Request
        </span>
        <span className="hidden sm:inline text-xs md:text-sm text-gray-200">
          Empower your 100+ employee team with Visaro HR &amp; Credit on Request for free.
        </span>
        <Link to="/survey" className="text-visaro-orange font-bold hover:underline text-xs md:text-sm shrink-0 flex items-center gap-0.5 ml-1">
          Claim Portal &rarr;
        </Link>
      </div>

      <Navbar />
      <HeroSection />
      
      {/* HR Platform Feature Showcase - Prominent Placement */}
      <HrPromoSection />

      {/* --- Section 1: Image Left, Text Right --- */}
      <section className="p-20 pt-10 md:pt-20 px-10 md:px-16 flex flex-col md:flex-row items-center gap-20 text-lg">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInLeft}
          className="flex-1 relative flex justify-center items-center"
        >
          <img
            src={require("../assets/images/visaro-vacation-man.png")}
            alt="girl-with-phone"
            className="rounded-3xl w-full h-auto object-cover shadow-lg"
          />
          <div className="absolute bottom-16 bg-white/80 border border-white gap-4 backdrop-blur-md rounded-3xl p-4 text-sm flex w-3/4 left-1/2 -translate-x-1/2">
            <img
              src={circle_logo}
              alt="visaro-logo-circle"
              className="h-10 self-center aspect-square bg-white rounded-full"
            />
            <div className="flex-1">
              <div className="text-visaro-orange">Yay! 🎉</div>
              <div className="text-gray-500">Your loan has been accepted</div>
            </div>
            <div className="text-gray-500">now</div>
          </div>
        </motion.div>
        
        <motion.div 
           initial="hidden"
           whileInView="visible"
           viewport={{ once: true, margin: "-100px" }}
           variants={fadeInRight}
           className="md:flex-[1.5] flex flex-col gap-8"
        >
          <div className="text-deepBlue text-4xl md:text-6xl font-bold font-aeonic">
            Take that business trip or that long-awaited vacation without “financial stress”
          </div>
        </motion.div>
      </section>

      
      <section className="p-20 pt-10 md:pt-20 px-10 md:px-16 flex flex-col items-center gap-20 text-lg">
        <div className="flex flex-col-reverse md:flex-row gap-20 md:gap-40 items-center">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInLeft}
            className="flex flex-1 md:flex-[1.5] flex-col gap-8"
          >
            <div className="text-deepBlue text-4xl md:text-6xl font-bold">
              At Visaro. We offer “Flexible Travel Financing” at No Extra Cost
            </div>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInRight}
            className="flex-1 relative flex justify-center items-center"
          >
            <img
              src={require("../assets/images/visaro-hand-with-phone.png")}
              alt="hand-with-phone"
              className="rounded-3xl"
            />
            <div className="absolute bottom-16 bg-white/80 border border-white gap-4 backdrop-blur-md rounded-3xl p-4 text-sm flex w-3/4 left-1/2 -translate-x-1/2">
              <img
                src={circle_logo}
                alt="visaro-logo-circle"
                className="h-10 self-center aspect-square bg-white rounded-full"
              />
              <div className="flex-1">
                <div className="text-visaro-orange">Pay Later</div>
                <div className="text-gray-500">
                  3 interest-free payments due every month starting today.
                </div>
              </div>
              <div className="text-gray-500">now</div>
            </div>
          </motion.div>
        </div>

        {/* Small Header */}
        <div className="flex flex-col md:flex-row gap-20 md:gap-40 items-center">
           <motion.div 
             initial={{ opacity: 0, y: 20 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.6 }}
             className="text-deepBlue text-4xl md:text-6xl font-bold"
           >
              How It Works
           </motion.div>
        </div>
      </section>

      <HowItWorksSection />
      <ExpandingHorizonSection />

      {/* --- Section 2 Part 2 --- */}
      <section className="p-20 pt-10 md:pt-20 px-10 md:px-16 flex flex-col items-center gap-20 text-lg">
        
        {/* Financial Tech: Image Left, Text Right */}
        <div className="flex flex-col md:flex-row gap-20 md:gap-40 items-center">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInLeft}
            className="flex-1 relative flex justify-center items-center"
          >
            <img
              src={require("../assets/images/visaro-padlock.png")}
              alt="lock"
              className="rounded-3xl"
            />
          </motion.div>
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInRight}
            className="flex-1 flex flex-col gap-8"
          >
            <div className="text-deepBlue text-4xl md:text-6xl font-bold">
              Financial Tech Solutions
            </div>
            <div>
              We have services from Digital Wallets, Loans, API Service, Smart
              Cards, E-commerce, Bills and so much more.
            </div>
            <Link
              to="/sign-up"
              className="flex items-center gap-2 text-visaro-orange font-medium"
            >
              <span>Coming Soon</span>
              <ArrowRight />
            </Link>
          </motion.div>
        </div>

        {/* Digital Solutions: Text Left, Image Right */}
        <div className="flex flex-col-reverse md:flex-row gap-20 md:gap-40 items-center">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInLeft}
            className="flex-1 flex flex-col gap-8"
          >
            <div className="text-deepBlue text-4xl md:text-6xl font-bold">
              Digital Solutions
            </div>
            <div>
              There is Digital Transformation, Digital Identity, Biometrics,
              E-governance, Smart City and so on.
            </div>
            <a
              href="https://merchant.visaro.ng"
              className="flex items-center gap-2 text-visaro-orange font-medium"
            >
              <span>Coming Soon</span>
              <ArrowRight />
            </a>
          </motion.div>
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInRight}
            className="flex-1 relative flex justify-center items-center"
          >
            <img
              src={require("../assets/images/visaro-city-bowl.png")}
              alt="lock"
              className="rounded-3xl"
            />
          </motion.div>
        </div>

        {/* Ed-Tech: Image Left, Text Right */}
        <div className="flex flex-col md:flex-row gap-20 md:gap-40 items-center">
          <motion.div 
             initial="hidden"
             whileInView="visible"
             viewport={{ once: true, margin: "-100px" }}
             variants={fadeInLeft}
             className="flex-1 relative flex justify-center items-center"
          >
            <img
              src={require("../assets/images/visaro-guy-door.png")}
              alt="lock"
            />
            <div className="absolute bottom-16 bg-white/80 border border-white gap-4 backdrop-blur-md rounded-3xl p-4 text-sm flex w-3/4 left-1/2 -translate-x-1/2">
              <img
                src={circle_logo}
                alt="visaro-logo-circle"
                className="h-10 self-center aspect-square bg-white rounded-full"
              />
              <div className="flex-1">
                <div className="text-visaro-orange">Harry</div>
                <div className="text-gray-500">Fund request accepted!</div>
              </div>
              <div className="text-gray-500">now</div>
            </div>
          </motion.div>
          <motion.div 
             initial="hidden"
             whileInView="visible"
             viewport={{ once: true, margin: "-100px" }}
             variants={fadeInRight}
             className="flex-1 flex flex-col gap-8"
          >
            <div className="text-deepBlue text-4xl md:text-6xl font-bold">
              Ed-Tech Solutions
            </div>
            <div>
              E-learning & Distant Learning management services, Web/Portal
              Designing and Smart Students ID.
            </div>
            <a
              href="https://merchant.visaro.ng"
              className="flex items-center gap-2 text-visaro-orange font-medium"
            >
              <span>Coming Soon</span>
              <ArrowRight />
            </a>
          </motion.div>
        </div>

        {/* Agro Services: Text Left, Image Right */}
        <div className="flex flex-col-reverse md:flex-row gap-20 md:gap-40 items-center">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInLeft}
            className="flex-1 flex flex-col gap-8"
          >
            <div className="text-deepBlue text-4xl md:text-6xl font-bold">
              Agro Services with a difference
            </div>
            <div>We also offer end-to-end agricultural solutions.</div>
            <a
              href="https://merchant.visaro.ng"
              className="flex items-center gap-2 text-visaro-orange font-medium"
            >
              <span>Coming Soon</span>
              <ArrowRight />
            </a>
          </motion.div>
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInRight}
            className="flex-1 relative flex justify-center items-center"
          >
            <img
              src={require("../assets/images/visaro-agro-plant.png")}
              alt="lock"
            />
            <div className="absolute bottom-16 bg-white/80 border border-white gap-4 backdrop-blur-md rounded-3xl p-4 text-sm flex w-3/4 left-1/2 -translate-x-1/2">
              <img
                src={circle_logo}
                alt="visaro-logo-circle"
                className="h-10 self-center aspect-square bg-white rounded-full"
              />
              {/* <div className="flex-1">
                <div className="text-visaro-orange">Pay Later</div>
                <div className="text-gray-500">
                  3 interest-free payments due every month starting today.
                </div>
              </div>
              <div className="text-gray-500">now</div> */}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mint Green Section - Simple Fade Up */}
      <motion.section 
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="bg-visaro-visaMintGreen p-20 px-10 md:px-32 flex flex-col items-center justify-center gap-8"
      >
        <div className="text-deepBlue text-3xl md:text-6xl text-center font-bold tracking-wide">
          We have over 170 million people without health insurance cover. The
          idea is to offer{" "}
          <span className="text-visaro-visaGreen">
            Visaro Pay (BNPL) service
          </span>{" "}
          to customers at the point of their needs: emergencies (Saving Lives).
        </div>
        <div className="text-lg font-medium text-center">
          *Our market fit or strategy is to partner with Health and Aviation
          partners.
        </div>
      </motion.section>
      
      <FAQSection />
      <Footer withLegalText />
    </div>
  );
};

export default Index;